<template>
  <component
    :is="tag"
    :href="link?.href"
    @click="link?.navigate"
    :class="[link?.href && 'hover:tw-shadow-lg']"
    class="tw-group tw-relative tw-shadow tw-min-h-36 tw-w-full tw-rounded-[4px] tw-bg-white tw-p-4 tw-transition-shadow tw-flex tw-flex-col tw-justify-between tw-content-start"
  >
    <!-- header -->
    <div class="te-relative tw-min-h-7 tw-h-auto tw-w-full">
      <div class="tw-grid tw-grid-cols-12 tw-gap-1">
        <div
          class="tw-flex tw-justify-start tw-items-baseline"
          :class="[cardTooltip ? 'tw-col-span-10' : 'tw-col-span-12']"
        >
          <template v-if="slots['icon']">
            <div
              :data-test-id="`${dataTestId}-title-icon`"
              v-tippy="{
                content: iconTooltip?.content,
                placement: iconTooltip?.placement,
              }"
              class="tw-mr-2 tw-h-6 tw-w-6 tw-rounded-full tw-border-2 tw-border-dorian-gray-100 tw-bg-white tw-p-0.5 tw-mt-0.5"
            >
              <slot name="icon"></slot>
            </div>
          </template>
          <template v-if="!link">
            <div
              class="tw-text-lg tw-font-semibold tw-text-dorian-gray-500"
              :data-test-id="`${dataTestId}-title-label`"
            >
              <slot name="title"></slot>
            </div>
          </template>
          <template v-else>
            <div
              class="tw-text-lg tw-font-semibold tw-text-dorian-gray-500 group-hover:tw-text-secondary-500 tw-transition-colors tw-self-center"
              :data-test-id="`${dataTestId}-title-label`"
            >
              <span>{{ link.label }}</span>
              <span class="tw-relative tw-inline-flex tw-ml-2">
                <svg
                  width="6"
                  height="12"
                  viewBox="0 0 8 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class=""
                >
                  <path
                    d="M1 1L7 7L1 13"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
            </div>
          </template>
        </div>
        <template v-if="cardTooltip">
          <div
            class="tw-col-span-2 tw-flex tw-justify-end tw-items-baseline tw-mt-1.5"
          >
            <span
              :data-test-id="`${dataTestId}-card-tooltip`"
              v-tippy="{
                content: cardTooltip?.content,
                placement: cardTooltip?.placement,
              }"
              class="tw-text-dorian-gray-200"
            >
              <svg
                width="15"
                height="14"
                viewBox="0 0 15 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.3335 5H7.34016M6.66683 7H7.3335V9.66667H8.00016M1.3335 7C1.3335 7.78793 1.48869 8.56815 1.79022 9.2961C2.09175 10.0241 2.5337 10.6855 3.09086 11.2426C3.64801 11.7998 4.30944 12.2417 5.0374 12.5433C5.76535 12.8448 6.54557 13 7.3335 13C8.12143 13 8.90164 12.8448 9.6296 12.5433C10.3576 12.2417 11.019 11.7998 11.5761 11.2426C12.1333 10.6855 12.5752 10.0241 12.8768 9.2961C13.1783 8.56815 13.3335 7.78793 13.3335 7C13.3335 5.4087 12.7014 3.88258 11.5761 2.75736C10.4509 1.63214 8.9248 1 7.3335 1C5.7422 1 4.21607 1.63214 3.09086 2.75736C1.96564 3.88258 1.3335 5.4087 1.3335 7Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
          </div>
        </template>
      </div>
    </div>
    <!-- footer -->
    <template v-if="slots['footer']">
      <div class="tw-relative">
        <slot name="footer"></slot>
      </div>
    </template>
  </component>
</template>

<script lang="ts">
type Link = {
  label: string;
  href: string;
  navigate: () => void;
};

type Tooltip = {
  content: string;
  placement: "top" | "bottom" | "left" | "right";
};

interface Props {
  iconTooltip?: Tooltip;
  cardTooltip?: Tooltip;
  link?: Link;
  dataTestId: string;
}
</script>

<script lang="ts" setup>
import { computed, toRefs, type VNode } from "vue";

const props = defineProps<Props>();
const { link, iconTooltip, cardTooltip, dataTestId } = toRefs(props);
const tag = computed(() => (link.value ? "a" : "div"));

const slots = defineSlots<{
  title?: () => VNode | VNode[];
  icon?: () => VNode | VNode[];
  footer?: () => VNode | VNode[];
}>();
</script>
